import { AnimatePresence, motion, Variants } from 'framer-motion'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { useMyMonitoringFindMyMonitoringUserByEmail } from '@apps-common/apis/my-monitoring/users'
import { useFindClientAccountByEmail } from '@apps-common/apis/portal/client-accounts'
import {
  MyMonitoringUserAddExistingForm,
  MyMonitoringUserAddNewForm
} from '@apps-common/apps/portal/pages/units/pages/manage/pages/users/modals/my-monitoring-modify-modal'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { ModalBody, ModalFooter } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.25
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.25
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.25
    }
  }
}

interface InputProps {
  email: string
}

interface IProps {
  unitId: string
  existingUserIds: number[]
  closeCb: (role: string) => void
}

export const MyMonitoringUserAddForm: React.FC<IProps> = ({ unitId, existingUserIds, closeCb }) => {
  const [searched, setSearched] = useState<boolean>(false)

  const useFindUserHook = useProjectHook(useFindClientAccountByEmail, useMyMonitoringFindMyMonitoringUserByEmail)

  const {
    data: account,
    mutate: findAccount,
    isPending: findIsPending,
    reset: resetFindAccount
  } = useFindUserHook(true, () => setSearched(true))

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch
  } = useForm<InputProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: ''
    }
  })

  const emailVal = watch('email')

  const resetForm = () => {
    setSearched(false)
    resetFindAccount()
  }

  const handleSearchEmail = ({ email }: InputProps) => {
    findAccount({ email })
  }

  const foundAccountExistsOnSite = account && existingUserIds.includes(account.id)

  return (
    <>
      {!searched && (
        <ModalBody classes='!pt-[110px] !pb-[20px]'>
          <motion.div key='search-users-form' initial='initial' animate='animate' exit='exit' variants={variants} className='w-full'>
            <form noValidate id='search-users-form' onSubmit={handleSubmit(handleSearchEmail)}>
              <div className='flex items-start justify-between gap-[10px]'>
                <div className='grow'>
                  <div className='relative'>
                    <InputSM
                      key='create-user-account-email'
                      type='text'
                      placeholder='Enter account email address'
                      label='Email'
                      id='create-user-account-email'
                      disabled={findIsPending}
                      registration={register('email', {
                        required: FORM_DEFAULT_REQUIRED,
                        pattern: {
                          value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                          message: 'Invalid email'
                        }
                      })}
                      errors={errors.email}
                    />
                    <AnimatePresence>
                      {emailVal && emailVal.length > 0 && (
                        <motion.button
                          initial='initial'
                          animate='animate'
                          exit='exit'
                          variants={variants}
                          type='button'
                          className='absolute right-[10px] top-[9px] text-text disabled:text-text-muted/60'
                          disabled={findIsPending}
                          onClick={() => reset()}
                        >
                          <commonIcons.close className='text-[1.3em]' />
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <ButtonRoundSm type='submit' form='search-users-form' className='action-success w-[34px] h-[34px] rounded-full'>
                  <commonIcons.search />
                </ButtonRoundSm>
              </div>
            </form>
          </motion.div>
        </ModalBody>
      )}
      {account && foundAccountExistsOnSite && searched && (
        <>
          <ModalBody classes='!pt-[110px]'>
            <p className='bg-surface rounded-xl p-6 jost-light text-text flex flex-col items-center justify-center gap-[20px]'>
              <div className='p-4 bg-warning text-on-waring rounded-full'>
                <commonIcons.toastInfo className='text-[2rem] mt-[2px] sm:mt-0' />
              </div>
              <div className='whitespace-pre-line'>
                User with email <span className='jost-medium'>{account.email}</span> already exists on site.
              </div>
            </p>
          </ModalBody>
          <ModalFooter>
            <ButtonRoundedSm
              type='button'
              loading={false}
              text='Close'
              className='action-neutral'
              callback={() => closeCb(ModalDismiss.Cancel)}
            />
          </ModalFooter>
        </>
      )}
      {!account && !foundAccountExistsOnSite && searched && (
        <MyMonitoringUserAddNewForm uId={unitId} email={emailVal} closeCb={closeCb} resetCb={resetForm} />
      )}
      {account && !foundAccountExistsOnSite && searched && (
        <MyMonitoringUserAddExistingForm uId={unitId} user={account} closeCb={closeCb} resetCb={resetForm} />
      )}
    </>
  )
}
